/* @flow */
/* eslint-disable @remind/remind/no-on-functions */
import cx from 'classnames';
import * as React from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { Container } from 'flux/utils';

import { ChatMessage, ChatGapMessage } from 'entities/ChatMessage';
import { markChange } from 'lib/Timer';
import { mountModal } from 'lib/modal';
import { promiseRelationship } from 'chat/utils/ChatPromises';
import { PromptNames } from 'constants/PromptConstants';
import { TOUCH_KEY as STREAM_SECTION_TOUCH_KEY } from 'chat/components/StreamSectionList';
import { touch } from 'actions/touchActions';
import ChatActions from 'chat/actions/ChatActions';
import ChatConstants from 'chat/constants/ChatConstants';
import ChatMembers from 'collections/ChatMembers';
import ChatMemberships from 'collections/ChatMemberships';
import ChatMessageActions from 'chat/actions/ChatMessageActions';
import ChatMessageStore from 'chat/stores/ChatMessageStore';
import ChatStore from 'chat/stores/ChatStore';
import ConnectionState from 'stores/ConnectionState';
import ConversationHeader from 'chat/components/ConversationHeader';
import ConversationHeaderSidebarButton from 'chat/components/ConversationHeaderSidebarButton';
import ConversationSidebar from 'chat/components/ConversationSidebar';
import ConversationViewComposer from 'chat/components/ConversationViewComposer';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import getRoleDescriptionForChat from 'lib/getRoleDescriptionForChat';
import Group from 'entities/Group';
import PhoneCallButton from 'phoneCalls/components/PhoneCallButton';
import { isFixableDenialReason } from 'phoneCalls/components/PhoneCallModal';
import CanMakePhoneCallsContainer from 'phoneCalls/containers/CanMakePhoneCallsContainer';
import Button, { ButtonSize } from 'components/ButtonDeprecated';
import Icon from 'components/Icon';
import CREATE_DISTANCE_LEARNING_SESSION_WITH_INVITE from 'chat/queries/CreateDistanceLearningSessionWithInviteMutation.gql';
import RemindAPI from 'lib/RemindAPI';
import type {
  createDistanceLearningSessionWithInvite,
  createDistanceLearningSessionWithInviteVariables,
} from 'chat/queries/__generated__/createDistanceLearningSessionWithInvite';

import PromptActions from 'actions/PromptActions';
import Buttons from 'components/Buttons';
import PromptStore from 'stores/PromptStore';
import StudentsParentsDialog from 'user/components/StudentsParentsDialog';
import ToastActions from 'actions/ToastActions';
import Chat from 'entities/Chat';
import UnreadStore from 'stores/UnreadStore';
import errorLogger from 'utils/errorLogger';
import {
  LanguageSetInputTargetType,
  MessageTranslationsInput,
} from '../../../__generated__/globalTypes';
import type { GetLanguageSet_translations_getLanguageSet_data_languageCounts as LanguageCounts } from '../../translations/queries/__generated__/GetLanguageSet';
import MetricTracking, { boolTagValue } from 'lib/MetricTracking';

import type { RouterState, LocationDescriptor } from 'lib/ReactRouterTypes';
import type { MessageType } from 'chat/lib/MessageInterfacePropType';
import type { V2File } from 'clients/api/fileUploads';

import {
  ChatStream,
  ChatStreamPlaceholder,
  addStreamSequenceItem,
  removeStreamSequenceItem,
  lastStreamItemSequenceNumber,
} from 'chat/components/ChatStream';

import './styles/ConversationView.scss';
import { getLanguageSets } from '../../translations/actions';

type State = {
  chat: Chat,
  chatMemberships: ChatMemberships,
  messages: MessageType[],
  sidebar: boolean,
  unreadCount: number,
  languageCounts: LanguageCounts[],
};

type Props = {
  params: {
    chatId: Backbone$Id,
    memberId: string,
    groupCode: string,
  },
  className?: ?string,
};

function getChatState(chat: Chat, messages: MessageType[]) {
  return {
    chat,
    chatMemberships: chat.getMemberships(),
    messages,
    unreadCount: chat.isNew() ? 0 : UnreadStore.getUnreadCount((chat.id: string)),
  };
}

const defaultState = {
  sidebar: false,
  languageCounts: [],
};

export class ConversationView extends React.Component<Props, State> {
  // eslint-disable-next-line @remind/remind/legacy-context
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
    group: PropTypes.instanceOf(Group).isRequired,
  };

  /* React-router hook set in the /new route
   */
  static onLeave() {
    ChatActions.destroyLocal();
  }

  static onEnter(
    nextState: RouterState,
    replaceWith: (location: LocationDescriptor) => void,
    callback: Function
  ) {
    const { params, location } = nextState;
    const { query } = location;
    const { memberId, chatId, groupCode } = params;

    if (chatId === 'stream') {
      replaceWith({ pathname: `/classes/${groupCode}/messages/stream`, query });
      callback();
      return;
    }

    if (memberId) {
      ConversationView.createLocalChat(memberId)
        .then(callback)
        .catch(errorLogger('ConversationView.createLocalChat failed'));
    } else if (chatId) {
      const chat = ChatStore.getChat(chatId);
      if (chat) {
        callback();
      } else {
        const onLoadChatError = () => {
          replaceWith({ pathname: `/classes/${groupCode}` });
          callback();
        };
        ChatActions.loadChat(chatId)
          .then(
            () => callback(),
            () => onLoadChatError()
          )
          .catch(e => {
            console.error('loadChat failure');
            console.warn(e);
          });
      }
    } else {
      // this includes group chats, for which the local is already created
      callback();
    }
  }

  // XXX move out of this component
  static createLocalChat(memberIdStr: string) {
    const memberId = parseInt(memberIdStr, 10);
    const currentMember = ChatMembers.buildChatMember(CurrentUser.getUser());
    return promiseRelationship(memberId).then(relationship => {
      const member = ChatMembers.buildChatMember(relationship.getRelatedUser());
      return ChatActions.loadLocal([member, currentMember], member.getName());
    });
  }

  static getStores() {
    return [ChatMessageStore, ChatStore, UnreadStore];
  }

  static calculateState(prevState: ?State, props: Props) {
    const { chatId } = props.params;

    const chat = chatId ? ChatStore.getChat(chatId) : null;
    const chatOrLocalChat = chat || ChatStore.getLocal();

    const nextMessages = chatId ? ChatMessageStore.getChatMessages(chatId).models.slice(0) : [];

    if (chatOrLocalChat) {
      const updates = getChatState(chatOrLocalChat, nextMessages);
      return { ...defaultState, ...prevState, ...updates };
    }

    // Do not change state for null chat
    return prevState;
  }

  componentDidMount() {
    markChange('chat_open', 'chat_shown');

    if (this.shouldShowStudentParentCreateGroupModal()) {
      // $FlowFixMe onRequestHide prop provided by mountModal
      mountModal(<StudentsParentsDialog onOkay={this.onStudentsParentsDialogOkay} />, this);
      PromptActions.recordDismissal(PromptNames.SP_CREATE_GROUP_MODAL);
    } else {
      this.focusComposer();
    }
    this.updateLanguageSet();
    ConnectionState.on('reconnect', this.handleReconnect);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    // shallow state
    const shallowKeys = ['chat', 'sidebar', 'unreadCount'];

    for (const key of shallowKeys) {
      if (this.state[key] !== nextState[key]) {
        return true;
      }
    }

    if (!isEqual(this.props.params, nextProps.params)) {
      return true;
    }

    if (this.state.messages.length !== nextState.messages.length) {
      return true;
    }

    const oldChatMemberships = this.state.chatMemberships;
    const newChatMemberships = nextState.chatMemberships;
    const membershipsHaveChanged =
      oldChatMemberships !== newChatMemberships ||
      oldChatMemberships.length !== newChatMemberships.length ||
      (!!oldChatMemberships &&
        !!newChatMemberships &&
        oldChatMemberships.some(
          (o, i) => !isEqual(o.attributes, newChatMemberships.at(i).attributes)
        ));
    if (membershipsHaveChanged) {
      return true;
    }

    return false;
  }

  componentDidUpdate(prevProps: Props, prevState: Object) {
    if (prevProps.params.chatId === this.props.params.chatId) {
      const currentLastMessage = this.state.messages[this.state.messages.length - 1];
      const prevLastMessage = prevState.messages[prevState.messages.length - 1];
      if (currentLastMessage !== prevLastMessage) {
        markChange('chat_open', 'last_message_shown');
      }
    } else {
      // focusComposer() does not work without setTimeout(,1) after DOM update
      setTimeout(() => {
        this.focusComposer();
      }, 1);
      this.updateLanguageSet();
      // chatId changed
      markChange('chat_open', 'chat_shown');
    }
  }

  componentWillUnmount() {
    ConnectionState.removeListener('reconnect', this.handleReconnect);
  }

  toggleSidebar = () => {
    this.setState(({ sidebar }) => ({ sidebar: !sidebar }));
  };

  onStudentsParentsDialogOkay = () => {
    this.context.router.push('/classes'); // almost certainly incorrect
  };

  getLanguageSetInput = () => {
    if (!this.state.chat) {
      return null;
    }
    const { chat } = this.state;
    const chatType = chat.getType();
    if (chatType === 'individual') {
      const otherMember = chat.getOtherMember();
      if (otherMember && otherMember.uuid) {
        return [
          {
            targetUuid: otherMember.uuid,
            targetType: LanguageSetInputTargetType.individual,
          },
        ];
      }
    }
    if (chatType === 'group') {
      return [
        {
          targetUuid: chat.id,
          targetType: LanguageSetInputTargetType.groupChat,
        },
      ];
    }
    return undefined;
  };

  updateLanguageSet = async () => {
    const languageSetInput = this.getLanguageSetInput();
    if (languageSetInput) {
      const result = await getLanguageSets(languageSetInput);
      if (result.success) {
        this.setState({
          languageCounts: result.items,
        });
      }
    }
  };

  // Invoked upon message send in a new chat thread, this creates the chat thread
  // on the server, and destroys the local chat
  initialize = (text: string, file?: V2File) => {
    const sender = CurrentUser.getUser();
    const { group } = this.context;
    const originGroupId = group.id > 0 ? group.id : null;
    const message = ChatMessage.create(
      text,
      file,
      this.state.chat,
      sender,
      0,
      'text',
      null,
      originGroupId
    );
    return ChatActions.createChat(this.state.chat, message)
      .then(chat => {
        this.context.router.replace(`/classes/${group.getCodeOrError()}/messages/${chat.id}`);
        ChatActions.destroyLocal();
      })
      .catch(error => {
        if (error && ['no_solo_chats', 'max_memberships'].indexOf(error.code) > -1) {
          ToastActions.showErrorToast(error.message);
        } else {
          throw error;
        }
      });
  };

  // If no chatId is passed in the url, this must be a new chat thread
  isNewConversation = () => {
    const { params } = this.props;
    return !params || !params.chatId;
  };

  loadMoreMessages = (message: ChatGapMessage) => {
    if (!message.getChatId()) {
      console.error(
        new Error(
          'ConversationView is attempting to load messages for a gap message having no chatId!'
        ),
        message
      );
    }

    ChatMessageActions.fillGap(message);
  };

  focusComposer = () => {
    // Check for truthy refs.composer is defensive, focusComposer is used in a setTimeout()
    if (ChatStore.getFocusView() === ChatConstants.FocusableViews.COMPOSER && this.refs.composer) {
      this.refs.composer.focus();
    }
  };

  handleReconnect = () => {
    const { chatId } = this.props.params;
    if (chatId) {
      ChatMessageActions.loadChatMessages(chatId);
    }
  };

  openSideBar = () => {
    if (!this.state.sidebar) {
      this.toggleSidebar();
    }
  };

  sendMessage = (text: string, file?: V2File, translations?: MessageTranslationsInput) => {
    const sender = CurrentUser.getUser();
    const { chat } = this.state;
    const { group } = this.context;
    const hasAttachment = file !== undefined;
    const hasPLTTranslations = translations?.translated;
    // we should always have a `group` here from the context, but we do this check because we don't always
    // mock the context in our tests
    const originGroupId = group && group.id > 0 ? group.id : null;
    const seq = lastStreamItemSequenceNumber(chat.id.toString()) + 1;

    const message = ChatMessage.create(text, file, chat, sender, seq, 'text', null, originGroupId);

    addStreamSequenceItem(chat.id.toString(), message.toGraphQL());
    ChatMessageActions.sendMessage(message, translations).then(
      () => {
        MetricTracking.increment('stream.chat.view.sent.success', {
          tags: {
            has_attachment: boolTagValue(hasAttachment),
            has_plt_translations: boolTagValue(hasPLTTranslations),
          },
        });
        touch(STREAM_SECTION_TOUCH_KEY);
      },
      err => {
        MetricTracking.increment('stream.chat.view.sent.failed', {
          tags: {
            has_attachment: boolTagValue(hasAttachment),
            has_plt_translations: boolTagValue(hasPLTTranslations),
          },
        });
        const errorMessage = err.message || 'Error sending message.';
        ToastActions.showErrorToast(errorMessage);
        removeStreamSequenceItem(chat.id.toString(), message.id);
        console.error('ChatMessageActions.sendMessage failed');
      }
    );
  };

  shouldShowStudentParentCreateGroupModal = () => {
    return (
      CurrentUser.getUser()
        .getPromptsInventory()
        .find(i => i.get('action') === PromptNames.SP_CREATE_GROUP_MODAL) &&
      !PromptStore.hasDismissed(PromptNames.SP_CREATE_GROUP_MODAL)
    );
  };

  chatHasSidebar = () => {
    return !this.state.chat.isNew();
  };

  updateUnread = () => {
    if (this.state.unreadCount <= 0) {
      return;
    }

    const seq = parseInt(lastStreamItemSequenceNumber(this.state.chat.id.toString()), 10);

    if (seq) {
      this.state.chat.markAsReadForAssociatedGroups(UnreadStore);
      ChatActions.markReadUpto(this.state.chat.id, seq);
    }
  };

  createDistanceLearningSession = () => {
    RemindAPI.execute<
      createDistanceLearningSessionWithInvite,
      createDistanceLearningSessionWithInviteVariables
    >({
      mutation: CREATE_DISTANCE_LEARNING_SESSION_WITH_INVITE,
      variables: { streamUuid: this.state.chat.id },
    })
      .then((res: createDistanceLearningSessionWithInvite) => {
        if (res.createDistanceLearningSessionWithInvite.error) {
          errorLogger(
            `ConversationView — could not create distance learning session invite — ${res.createDistanceLearningSessionWithInvite.error.message}`
          );
        }
      })
      .catch(errorLogger('ConversationView — could not create distance learning session invite'));
  };

  renderSideBar = () => {
    if (!this.state.sidebar) {
      return null;
    }

    const { group } = this.context;
    const { chat } = this.state;

    return (
      <ConversationSidebar className="ConversationView-body-sidebar" chat={chat} group={group} />
    );
  };

  renderPhoneButton = () => {
    const { chat } = this.state;
    const otherMember = chat.getOtherMember();
    if (!otherMember) {
      return <ErrorReporter error="ConversationView - renderPhoneButton missing otherMember" />;
    }
    const otherMemberUuid = otherMember.uuid;
    if (!otherMemberUuid) {
      return <ErrorReporter error="ConversationView - renderPhoneButton missing otherMemberUuid" />;
    }
    return (
      <CanMakePhoneCallsContainer>
        {({ canMakePhoneCalls }) => {
          if (canMakePhoneCalls) {
            return <PhoneCallButton calleeName={otherMember.name} calleeUuid={otherMemberUuid} />;
          }
          return null;
        }}
      </CanMakePhoneCallsContainer>
    );
  };

  renderDistanceLearningButton = () => {
    return (
      <Button
        aria-label="Create distance learning session"
        size={ButtonSize.xxsmall}
        color="mascot"
        level="secondary"
        onClick={this.createDistanceLearningSession}>
        <Icon name="video-camera" size="medium" />
      </Button>
    );
  };

  renderHeaderButtons = () => {
    const { chat } = this.state;
    const chatCanPhone = chat.canPhone() || isFixableDenialReason(chat.getCanPhoneDenialReason());
    const canShowDistanceLearning = CurrentUser.getUser().hasFeature(
      '2020.aug.web.host_distance_learning'
    );

    return (
      <Buttons>
        {chatCanPhone && this.renderPhoneButton()}
        {canShowDistanceLearning && this.renderDistanceLearningButton()}
        {this.chatHasSidebar() && (
          <ConversationHeaderSidebarButton
            chat={chat}
            className="ConversationView-header-sidebarButton"
            sidebar={false}
            onSideBarToggle={this.toggleSidebar}
          />
        )}
      </Buttons>
    );
  };

  render() {
    const { chat, languageCounts } = this.state;
    if (!chat) {
      return null;
    }

    const otherMember = chat.getOtherMember();
    const otherMemberUuid = otherMember && otherMember.uuid;

    return (
      <div className={cx('ConversationView', this.props.className)}>
        <div ref="header" data-test-selector="conversation-header">
          <ConversationHeader
            chat={chat}
            otherUserUuid={otherMemberUuid}
            sidebar={this.state.sidebar}
            subtitle={getRoleDescriptionForChat(chat, CurrentUser.getUserId())}
            onTitleClicked={this.chatHasSidebar() ? this.toggleSidebar : undefined}>
            {this.renderHeaderButtons()}
          </ConversationHeader>
        </div>
        <div className="ConversationView-body">
          <div className="ConversationView-body-wrap">
            {chat && chat.id ? (
              <ChatStream
                key={chat.id.toString()}
                uuid={chat.id.toString()}
                onScrollToBottom={this.updateUnread}
              />
            ) : (
              <ChatStreamPlaceholder />
            )}
            <ConversationViewComposer
              ref="composer"
              className="ConversationView-body-composer"
              chat={chat}
              // $FlowFixMe
              onMessageSend={this.isNewConversation() ? this.initialize : this.sendMessage}
              openSideBar={this.openSideBar}
              languageCounts={languageCounts}
            />
          </div>
          {this.renderSideBar()}
        </div>
      </div>
    );
  }
}

export default Container.create(ConversationView, { withProps: true });
